// Copyright 2026 The Simlin Authors. All rights reserved.
// Use of this source code is governed by the Apache License,
// Version 2.0, that can be found in the LICENSE file.

import * as React from 'react';

import { Link } from 'wouter';
import clsx from 'clsx';
import {
  AppBar,
  Button,
  ImageList,
  ImageListItem,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Toolbar,
  Avatar,
  AccountCircleIcon,
  MenuIcon,
} from '@simlin/diagram';

import { canCreate } from '@simlin/core/tier-policy';

import { NewProject } from './NewProject';
import { ContentLibrary } from './ContentLibrary';
import { TierBadge } from './TierBadge';
import { WelcomeDialog } from './WelcomeDialog';
import { SettingsDialog } from './SettingsDialog';
import { hasSeenWelcome, markWelcomeSeen, firstName } from './welcome-state';
import { Preferences, loadPreferences, savePreferences } from './preferences';
import { Project } from './Project';
import { User } from './User';

import markUrl from './logo-mark.svg';
import { getStoredTheme, toggleTheme, applyTheme, Theme } from './theme';

import styles from './Home.module.css';
import typography from './typography.module.css';

interface HomeState {
  anchorEl?: HTMLElement;
  projects: readonly Project[];
  theme: Theme;
  libraryOpen: boolean;
  welcomeOpen: boolean;
  // CO-3: the Settings modal (off the dashboard menu) and the local-first
  // editor preferences it edits.
  settingsOpen: boolean;
  preferences: Preferences;
  // False until the first project fetch resolves, so the empty state doesn't
  // flash before the list arrives.
  projectsLoaded: boolean;
}

function localStore(): Storage | undefined {
  return typeof window !== 'undefined' ? window.localStorage : undefined;
}

interface HomeProps {
  user: User;
  isNewProject: boolean;
  onNewProjectDone?: () => void;
  onLogout: () => void;
}

const AnchorOrigin = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};

class Home extends React.Component<HomeProps, HomeState> {
  state: HomeState;

  constructor(props: HomeProps) {
    super(props);

    this.state = {
      anchorEl: undefined,
      projects: [],
      theme: getStoredTheme(),
      libraryOpen: false,
      welcomeOpen: false,
      settingsOpen: false,
      preferences: loadPreferences(localStore()),
      projectsLoaded: false,
    };
  }

  componentDidMount(): void {
    // Kick off the initial project fetch only after the component is
    // mounted.  Previously this ran from the constructor (via
    // setTimeout) and the fetch could resolve before React committed
    // the mount, causing setState to fire on an unmounted component.
    void this.getProjects();

    // D7.3: show the one-time welcome on the dashboard (not the /new form) for a
    // user who hasn't dismissed it. Per-user localStorage so it shows once.
    const storage = typeof window !== 'undefined' ? window.localStorage : undefined;
    if (!this.props.isNewProject && !hasSeenWelcome(this.props.user.id, storage)) {
      this.setState({ welcomeOpen: true });
    }
  }

  handleWelcomeDismiss = () => {
    markWelcomeSeen(this.props.user.id, typeof window !== 'undefined' ? window.localStorage : undefined);
    this.setState({ welcomeOpen: false });
  };

  handleWelcomeStart = () => {
    // The Link in the dialog handles navigation to /new; we just record the
    // dismissal and close so the welcome doesn't reappear over the New Project form.
    markWelcomeSeen(this.props.user.id, typeof window !== 'undefined' ? window.localStorage : undefined);
    this.setState({ welcomeOpen: false });
  };

  getProjects = async (): Promise<void> => {
    try {
      const response = await fetch('/api/projects', { credentials: 'same-origin' });
      const status = response.status;
      if (!(status >= 200 && status < 400)) {
        console.log("couldn't fetch projects.");
        return;
      }
      const projects = (await response.json()) as Project[];
      this.setState({
        projects,
      });
    } finally {
      // Mark loaded on success or failure so the dashboard stops showing the
      // loading placeholder and renders the grid or the empty state.
      this.setState({ projectsLoaded: true });
    }
  };

  handleClose = () => {
    this.setState({
      anchorEl: undefined,
    });
  };

  handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({
      anchorEl: event.currentTarget,
    });
  };

  handleLogout = () => {
    // Close the menu before invoking the parent handler so the menu does
    // not flash visible during the React tree swap to <Login>.
    this.setState({ anchorEl: undefined });
    this.props.onLogout();
  };

  handleToggleTheme = () => {
    // Flip the document-level data-theme + localStorage, then mirror the
    // new value in component state so the MenuItem label updates on the
    // next render.  Close the menu so the user sees the new theme apply
    // without the dropdown lingering over it.
    const next = toggleTheme();
    this.setState({ theme: next, anchorEl: undefined });
  };

  handleOpenLibrary = () => {
    // Open the content library modal and close the dropdown menu.
    this.setState({ libraryOpen: true, anchorEl: undefined });
  };

  handleOpenSettings = () => {
    // Open the Settings modal (CO-3) and close the dropdown menu.
    this.setState({ settingsOpen: true, anchorEl: undefined });
  };

  handleCloseSettings = () => {
    this.setState({ settingsOpen: false });
  };

  handleThemeChange = (theme: Theme) => {
    // Explicit set from the Settings dialog (vs the menu's toggle). Applies the
    // document-level data-theme + localStorage, then mirrors it in state so the
    // menu label and the dialog checkbox both track it.
    applyTheme(theme);
    this.setState({ theme });
  };

  handlePreferencesChange = (preferences: Preferences) => {
    // Persist immediately (local-first) so the choice survives a reload; the
    // editor reads these on open.
    savePreferences(preferences, localStore());
    this.setState({ preferences });
  };

  handleCloseLibrary = () => {
    this.setState({ libraryOpen: false });
  };

  handleProjectCreated = (project: Project) => {
    window.location.pathname = '/' + project.id;
  };

  getGridListCols = () => {
    // TODO: this should be 1 on small screens, but useMediaQuery doesn't
    //       work in class components, only function components.
    return 2;
  };

  newProjectForm() {
    return (
      <div className={styles.newProjectForm}>
        <div className={styles.centeredFlex}>
          <div>
            <NewProject user={this.props.user} onProjectCreated={this.handleProjectCreated} />
          </div>
        </div>
      </div>
    );
  }

  // D7.5 empty state: a new user (or anyone who's deleted everything) gets a
  // branded panel that routes into the first model rather than a blank grid.
  emptyState() {
    return (
      <div className={styles.emptyState}>
        <h2 className={typography.heading2}>Start your first model</h2>
        <p className={styles.emptyBody}>
          You don&rsquo;t have any projects yet. Begin from a curated template, or start from a blank canvas.
        </p>
        <div className={styles.emptyActions}>
          <Button variant="contained" color="primary" onClick={this.handleOpenLibrary}>
            Browse templates
          </Button>
          {canCreate(this.props.user.tier) ? (
            <Link to="/new" className={styles.actionLink}>
              <Button variant="outlined" color="primary">
                New project
              </Button>
            </Link>
          ) : (
            <span title="Upgrade to a Student plan to create and save projects">
              <Button variant="outlined" color="primary" disabled>
                New project
              </Button>
            </span>
          )}
        </div>
      </div>
    );
  }

  projects() {
    const { projects, projectsLoaded } = this.state;

    // Don't render the empty state until the fetch resolves, or it flashes
    // before the (often non-empty) list arrives.
    if (!projectsLoaded) {
      return <div className={styles.projectGrid} />;
    }
    if (projects.length === 0) {
      return this.emptyState();
    }

    return (
      <div className={styles.projectGrid}>
        <div className={styles.dashboardHeader}>
          <h2 className={typography.heading5}>Your projects</h2>
        </div>
        <ImageList cols={this.getGridListCols()} gap={0}>
          {projects.map((project) => (
            <ImageListItem key={project.id} style={{ height: 'auto' }}>
              <Link to={`/${project.id}`} className={styles.modelLink}>
                <Paper className={styles.paper} elevation={1}>
                  <div className={styles.preview}>
                    <img src={`/api/preview/${project.id}`} alt="model preview" className={styles.previewImg} />
                  </div>
                  <h3 className={typography.heading5}>{project.displayName}</h3>
                  <p>{project.description}&nbsp;</p>
                </Paper>
              </Link>
            </ImageListItem>
          ))}
        </ImageList>
      </div>
    );
  }

  render() {
    const { anchorEl } = this.state;
    const { photoUrl } = this.props.user;
    const open = Boolean(anchorEl);

    const account = photoUrl ? (
      <Avatar alt={this.props.user.displayName} src={photoUrl} className={styles.avatar} />
    ) : (
      <AccountCircleIcon />
    );

    const content = this.props.isNewProject ? this.newProjectForm() : this.projects();

    return (
      <div className={clsx(styles.root)}>
        <AppBar position="static">
          <Toolbar variant="dense">
            <IconButton className={styles.menuButton} color="inherit" aria-label="Menu" edge="start" size="small">
              <MenuIcon />
            </IconButton>
            <h6 className={clsx(typography.heading6, typography.colorInherit, styles.flex)}>
              <Link to="/" className={clsx(styles.modelLink, styles.brand)} aria-label="Systems Analyzer — Home">
                <img src={markUrl} alt="" aria-hidden="true" className={styles.brandMark} />
                <span className={styles.brandText}>Systems Analyzer</span>
              </Link>
              <span className={styles.tagline}>Understand Systems. Drive Change. Create Coherence.</span>
            </h6>
            <div className={styles.toolbarActions}>
              <TierBadge tier={this.props.user.tier} />
              {canCreate(this.props.user.tier) ? (
                <Link to="/new" className={styles.modelLink}>
                  <Button variant="outlined" className={styles.newProjectButton}>
                    New Project
                  </Button>
                </Link>
              ) : (
                // D7.2 upgrade affordance: a sub-Student tier cannot save, so the
                // create action is disabled before the user can hit the 403 from
                // the server gate. The wrapping span carries the tooltip because a
                // disabled button doesn't receive hover events.
                <span title="Upgrade to a Student plan to create and save projects">
                  <Button variant="outlined" className={styles.newProjectButton} disabled>
                    New Project
                  </Button>
                </span>
              )}

              <IconButton
                className={styles.profileIcon}
                aria-owns={open ? 'menu-appbar' : undefined}
                aria-haspopup="true"
                onClick={this.handleMenu}
                color="inherit"
                size="small"
              >
                {account}
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl ?? null}
                anchorOrigin={AnchorOrigin}
                transformOrigin={AnchorOrigin}
                open={open}
                onClose={this.handleClose}
              >
                <MenuItem onClick={this.handleToggleTheme}>
                  Switch to {this.state.theme === 'dark' ? 'Light' : 'Dark'} mode
                </MenuItem>
                <MenuItem onClick={this.handleOpenSettings}>Settings</MenuItem>
                <MenuItem onClick={this.handleOpenLibrary}>Browse library</MenuItem>
                <MenuItem onClick={this.handleLogout}>Logout</MenuItem>
              </Menu>
            </div>
          </Toolbar>
        </AppBar>
        {content}
        <ContentLibrary open={this.state.libraryOpen} onClose={this.handleCloseLibrary} />
        <WelcomeDialog
          open={this.state.welcomeOpen}
          userName={firstName(this.props.user.displayName)}
          onDismiss={this.handleWelcomeDismiss}
          onStart={this.handleWelcomeStart}
        />
        <SettingsDialog
          open={this.state.settingsOpen}
          theme={this.state.theme}
          preferences={this.state.preferences}
          onThemeChange={this.handleThemeChange}
          onPreferencesChange={this.handlePreferencesChange}
          onClose={this.handleCloseSettings}
        />
      </div>
    );
  }
}

export default Home;
